/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Rating } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useRouter, useSearchParams } from "next/navigation";
import { Product } from "@/types/General";
import WishlistButton from "../WishlistButton";
import { useAddToCartMutation } from "@/service/cart";
import { useAppSelector } from "@/lib/hook";
import { getToken } from "@/lib/slices/authSlice";
import { getFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import toast from "react-hot-toast";
import { getDeliveryChannel } from "@/utils/deliveryMode";

const CustomNextArrow = (props: any) => {
  const { className, onClick } = props;
  return (
    <div
      className={`custom-slick-arrow next-arrow ${className}`}
      onClick={onClick}
    >
      <KeyboardArrowRightIcon />
    </div>
  );
};

const CustomPrevArrow = (props: any) => {
  const { className, onClick } = props;
  return (
    <div
      className={`custom-slick-arrow prev-arrow ${className}`}
      onClick={onClick}
    >
      <KeyboardArrowLeftIcon />
    </div>
  );
};

function HomeBestOfBest({ products = [], categoryId }: { products?: Product[]; categoryId?: number | string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [addToCart, { isLoading: isAddingToCart }] = useAddToCartMutation();
  const [addingProductId, setAddingProductId] = useState<number | null>(null);
  const reduxToken = useAppSelector(getToken);

  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    e.preventDefault();

    const currentToken = reduxToken || getFromStorage(STORAGE_KEYS.token);

    if (!currentToken) {
      toast.error("Please login to add items to cart");
      router.push("/auth/login");
      return;
    }

    // Validate product_id and store_id
    const productId = product?.product_id;
    const storeId = product?.store_id;

    if (!productId) {
      toast.error("Invalid product: Product ID is missing");
      return;
    }

    if (!storeId) {
      toast.error("Invalid product: Store ID is missing");
      return;
    }

    // Set loading state for this specific product
    setAddingProductId(productId);

    try {
      const deliveryChannel = getDeliveryChannel(searchParams);
      const payload = {
        product_id: productId,
        store_id: storeId,
        qty: 1,
        channel: deliveryChannel,
      };

      const res = await addToCart(payload).unwrap();
      if (res?.statusCode === 200) {
        toast.success("Added to cart successfully");
      } else {
        toast.error(res?.message || "Failed to add to cart");
      }
    } catch (error: any) {
      console.error("Add to cart error:", error);
      // Don't show generic error if it's a 500 error that will redirect
      if (error?.status !== 500 && error?.originalStatus !== 500) {
        toast.error(error?.data?.message || "Failed to add to cart");
      }
    } finally {
      // Clear loading state after a short delay to ensure popup is visible
      setTimeout(() => {
        setAddingProductId(null);
      }, 500);
    }
  };

  // Don't render if no products
  if (!products || products.length === 0) {
    return null;
  }

  // Get category ID from prop or from first product's category
  const resolvedCategoryId = categoryId || products?.[0]?.category?.id;

  type Slider = any;
  const sliderRef = useRef<Slider | null>(null);
  // const settings = {
  //   infinite: products?.length > 6,
  //   slidesToShow: 6,
  //   slidesToScroll: 1,
  //   arrows: true,
  //   nextArrow: <CustomNextArrow />,
  //   prevArrow: <CustomPrevArrow />,
  //   dots: false,
  //   responsive: [
  //     {
  //       breakpoint: 1200,
  //       settings: {
  //         slidesToShow: 4,
  //         slidesToScroll: 1,
  //         arrows: true,
  //         dots: true,
  //         infinite: products?.length > 4,
  //       },
  //     },
  //     {
  //       breakpoint: 992,
  //       settings: {
  //         slidesToShow: 3,
  //         slidesToScroll: 1,
  //         arrows: true,
  //         dots: true,
  //         infinite: products?.length > 3,
  //       },
  //     },
  //   ],
  // };
  const settings = {
    infinite: true,
    slidesToShow: 5,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
    dots: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
          infinite: true,
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
          infinite: true,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
          infinite: true,
        },
      },
    ],
  };
  return (
    <>
      <section className="home_best_sc product_sc">
        <div className="container">
          <div className="s_head flex hd_4">
            <h2>Today's Best Deals</h2>
            <div className="rt">
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const url = resolvedCategoryId
                    ? `/product/product-category?category_id=${resolvedCategoryId}&type=bestOfBest`
                    : "/product/product-category?type=bestOfBest";
                  router.push(url);
                }}
              >
                More <KeyboardArrowRightIcon />
              </Button>
            </div>
          </div>

          <Slider ref={sliderRef} {...settings} className="productBest_list">
            {products?.map((product, index) => (
              <div
                className="productBest_item"
                key={`home-best-${product?.product_id || 'no-id'}-${product?.store_id || 'no-store'}-${index}`}
              >
                <Box
                  component="a"
                  className="overlay_click"
                  onClick={() =>
                    router.push(
                      `/product/detail/?store_id=${product?.store_id || ""}&product_id=${product?.product_id || ""}`
                    )
                  }
                ></Box>

                <figure>
                  {/* Only keep the dynamic product image */}
                  <img src={product.image} alt={product?.product_name} />

                  <WishlistButton
                    productId={product.product_id}
                    storeId={product.store_id}
                    isWishlist={product.is_wishlist}
                  />

                  <Box 
                    component="a" 
                    className="add"
                    onClick={(e) => handleAddToCart(e, product)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: addingProductId === product.product_id ? "wait" : "pointer",
                      opacity: addingProductId === product.product_id ? 0.7 : 1,
                      pointerEvents: addingProductId === product.product_id ? "none" : "auto",
                    }}
                  >
                    {addingProductId === product.product_id && isAddingToCart ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      "Add"
                    )}
                  </Box>
                </figure>

                <div className="card_info">
                  <h3>{product?.product_name || "-"}</h3>

                  <p className="price v2">
                    <ins>₹{product?.price?.store_price}</ins>
                    <del>₹{product?.price?.mrp}</del>
                    {product?.price?.percentage_off ? (
                      <span>{product?.price?.percentage_off}% OFF</span>
                    ) : <span>0% OFF</span>}
                  </p>

                  <p className="review">
                    {product?.average_rating ? Number(product.average_rating).toFixed(1) : '0.0'}
                    <Rating name="read-only" value={1} max={1} readOnly />
                    <span>({product?.review_count} Reviews)</span>
                  </p>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>
    </>
  );
}

export default HomeBestOfBest;
